/**
 * Menu Service with Pagination
 * Provides paginated API calls for menu items, categories, and addons
 */

import { ApiResponse as APIResponse } from '../../shared/types';
import {
  Addon,
  MenuCategory,
  MenuItem,
  MenuItemConfiguredFiscalSetup,
  MenuItemMissingFiscalSetup,
  MenuItemMissingRecipe,
} from '../types/menu';
import {
  AddonsFilterParams,
  CategoriesFilterParams,
  MenuCountsResponse,
  MenuItemsFilterParams,
  MissingFiscalSetupFilterParams,
  MissingRecipesFilterParams,
  PaginatedResponse,
} from '../types/pagination';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Fetch all menu items for an outlet directly from Supabase (online)
 * Used for CSV exports to bypass local offline limits.
 */
export const fetchAllMenuItemsForExport = async (
  outletId: string,
): Promise<APIResponse<MenuItem[]>> => {
  try {
    const { data, error } = await supabase
      .from('menu_items')
      .select(
        `
        *,
        menu_categories(name),
        departments(name)
      `,
      )
      .eq('outlet_id', outletId)
      .order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const items = (data || []).map((item: Record<string, unknown>) => {
      const menuCategories = item.menu_categories as { name?: string } | null;
      const departments = item.departments as { name?: string } | null;
      return {
        ...item,
        category_name: menuCategories?.name,
        department_name: departments?.name,
        menu_categories: undefined,
        departments: undefined,
      } as unknown as MenuItem;
    });

    return { success: true, data: items };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch items',
      data: undefined,
    };
  }
};

/**
 * Fetch paginated menu items for an outlet
 */
export const fetchPaginatedMenuItems = async ({
  outletId,
  categoryId,
  departmentId,
  status,
  search,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: MenuItemsFilterParams): Promise<
  APIResponse<PaginatedResponse<MenuItem>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    // Build the query. Omitting outletId means a company-wide view (e.g. a
    // super user viewing "All Outlets") — RLS already scopes menu_items reads
    // to the caller's own company, so an unfiltered query is safe and correct.
    let query = supabase
      .from('menu_items')
      .select(
        `
        *,
        menu_categories(name),
        departments(name)
      `,
        { count: 'exact' },
      )
      .order('name', { ascending: true })
      .range(from, to);

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    // Apply filters
    if (categoryId) {
      query = query.eq('category_id', categoryId);
    }

    if (departmentId) {
      query = query.eq('department_id', departmentId);
    }

    if (status) {
      query = query.eq('status', status);
    }

    if (search) {
      query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    // Transform the response to flatten joined fields
    const items = (data || []).map((item: Record<string, unknown>) => {
      const menuCategories = item.menu_categories as { name?: string } | null;
      const departments = item.departments as { name?: string } | null;
      return {
        ...item,
        category_name: menuCategories?.name,
        department_name: departments?.name,
        menu_categories: undefined,
        departments: undefined,
      } as unknown as MenuItem;
    });

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: items,
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch menu items',
      data: undefined,
    };
  }
};

/**
 * Fetch paginated categories for an outlet
 */
export const fetchPaginatedCategories = async ({
  outletId,
  status,
  search,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: CategoriesFilterParams): Promise<
  APIResponse<PaginatedResponse<MenuCategory>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('menu_categories')
      .select('*', { count: 'exact' })
      .eq('outlet_id', outletId)
      .order('display_order', { ascending: true })
      .range(from, to);

    if (status) {
      query = query.eq('status', status);
    }

    if (search) {
      query = query.ilike('name', `%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: data as MenuCategory[],
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch categories',
      data: undefined,
    };
  }
};

/**
 * Fetch paginated addons for an outlet
 */
export const fetchPaginatedAddons = async ({
  outletId,
  status,
  search,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: AddonsFilterParams): Promise<APIResponse<PaginatedResponse<Addon>>> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('addon_library')
      .select('*', { count: 'exact' })
      .eq('outlet_id', outletId)
      .order('name', { ascending: true })
      .range(from, to);

    if (status) {
      query = query.eq('status', status);
    }

    if (search) {
      query = query.ilike('name', `%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: data as Addon[],
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch addons',
      data: undefined,
    };
  }
};

/**
 * Fetch menu counts for an outlet (items, categories, addons, out of stock)
 * This is a lightweight endpoint that only returns counts
 */
export const fetchMenuCounts = async (
  outletId?: string | null,
): Promise<APIResponse<MenuCountsResponse>> => {
  try {
    // Omitting outletId means a company-wide view (e.g. a super user viewing
    // "All Outlets") — RLS already scopes these reads to the caller's own
    // company, so unfiltered counts are safe and correct. Each query is
    // built with a plain if/else (rather than a shared generic helper) since
    // Supabase's query builder generics blow up TS's instantiation depth
    // when wrapped in a generic function.
    let itemsQuery = supabase
      .from('menu_items')
      .select('*', { count: 'exact', head: true });
    let categoriesQuery = supabase
      .from('menu_categories')
      .select('*', { count: 'exact', head: true });
    let addonsQuery = supabase
      .from('addon_library')
      .select('*', { count: 'exact', head: true });
    let outOfStockQuery = supabase
      .from('menu_items')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'out_of_stock');
    let activeQuery = supabase
      .from('menu_items')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active');
    let inactiveQuery = supabase
      .from('menu_items')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'inactive');

    if (outletId) {
      itemsQuery = itemsQuery.eq('outlet_id', outletId);
      categoriesQuery = categoriesQuery.eq('outlet_id', outletId);
      addonsQuery = addonsQuery.eq('outlet_id', outletId);
      outOfStockQuery = outOfStockQuery.eq('outlet_id', outletId);
      activeQuery = activeQuery.eq('outlet_id', outletId);
      inactiveQuery = inactiveQuery.eq('outlet_id', outletId);
    }

    // Run all count queries in parallel
    const [
      itemsResult,
      categoriesResult,
      addonsResult,
      outOfStockResult,
      activeResult,
      inactiveResult,
    ] = await Promise.all([
      itemsQuery,
      categoriesQuery,
      addonsQuery,
      outOfStockQuery,
      activeQuery,
      inactiveQuery,
    ]);

    return {
      success: true,
      data: {
        totalItems: itemsResult.count || 0,
        totalCategories: categoriesResult.count || 0,
        totalAddons: addonsResult.count || 0,
        outOfStockItems: outOfStockResult.count || 0,
        activeItems: activeResult.count || 0,
        inactiveItems: inactiveResult.count || 0,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch menu counts',
      data: undefined,
    };
  }
};

/**
 * Search menu items by name, category name, or department name
 */
export const searchMenuItems = async ({
  outletId,
  search,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: {
  outletId: string;
  search: string;
  page?: number;
  pageSize?: number;
}): Promise<APIResponse<PaginatedResponse<MenuItem>>> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    // First, get category IDs that match the search
    const { data: matchingCategories } = await supabase
      .from('menu_categories')
      .select('id')
      .eq('outlet_id', outletId)
      .ilike('name', `%${search}%`);

    const categoryIds = matchingCategories?.map((c) => c.id) || [];

    // Get department IDs that match the search
    const { data: matchingDepartments } = await supabase
      .from('departments')
      .select('id')
      .eq('outlet_id', outletId)
      .ilike('name', `%${search}%`);

    const departmentIds = matchingDepartments?.map((d) => d.id) || [];

    // Build OR conditions for the search
    const orConditions = [
      `name.ilike.%${search}%`,
      `description.ilike.%${search}%`,
    ];

    if (categoryIds.length > 0) {
      orConditions.push(`category_id.in.(${categoryIds.join(',')})`);
    }

    if (departmentIds.length > 0) {
      orConditions.push(`department_id.in.(${departmentIds.join(',')})`);
    }

    const query = supabase
      .from('menu_items')
      .select(
        `
        *,
        menu_categories(name),
        departments(name)
      `,
        { count: 'exact' },
      )
      .eq('outlet_id', outletId)
      .or(orConditions.join(','))
      .order('name', { ascending: true })
      .range(from, to);

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    // Transform the response to flatten joined fields
    const items = (data || []).map((item: Record<string, unknown>) => {
      const menuCategories = item.menu_categories as { name?: string } | null;
      const departments = item.departments as { name?: string } | null;
      return {
        ...item,
        category_name: menuCategories?.name,
        department_name: departments?.name,
        menu_categories: undefined,
        departments: undefined,
      } as unknown as MenuItem;
    });

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: items,
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to search menu items',
      data: undefined,
    };
  }
};

/**
 * Fetch paginated menu items that have no active recipe, from
 * menu_items_missing_recipes_view (see
 * supabase/migrations/20260731090000_create_menu_items_missing_recipes_view.sql).
 * Dashboard/reporting concern — direct online Supabase query, same pattern
 * as fetchPaginatedMenuItems above.
 */
export const fetchMenuItemsMissingRecipes = async ({
  outletId,
  search,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: MissingRecipesFilterParams): Promise<
  APIResponse<PaginatedResponse<MenuItemMissingRecipe>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('menu_items_missing_recipes_view')
      .select('*', { count: 'exact' })
      .range(from, to);

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    if (search) {
      query = query.ilike('menu_item_name', `%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: (data || []) as MenuItemMissingRecipe[],
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch menu items missing recipes',
      data: undefined,
    };
  }
};

export const fetchMenuItemsMissingFiscalSetup = async ({
  outletId,
  search,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: MissingFiscalSetupFilterParams): Promise<
  APIResponse<PaginatedResponse<MenuItemMissingFiscalSetup>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('menu_items_missing_fiscal_setup_view')
      .select('*', { count: 'exact' })
      .range(from, to);

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    if (search) {
      query = query.ilike('menu_item_name', `%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: (data || []) as MenuItemMissingFiscalSetup[],
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch menu items missing fiscal setup',
      data: undefined,
    };
  }
};

export const fetchMenuItemsConfiguredFiscalSetup = async ({
  outletId,
  search,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: MissingFiscalSetupFilterParams): Promise<
  APIResponse<PaginatedResponse<MenuItemConfiguredFiscalSetup>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('menu_items_configured_fiscal_setup_view')
      .select('*', { count: 'exact' })
      .range(from, to);

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    if (search) {
      query = query.ilike('menu_item_name', `%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: (data || []) as MenuItemConfiguredFiscalSetup[],
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch configured fiscal setup items',
      data: undefined,
    };
  }
};

/**
 * Fetch every menu item missing a recipe for an outlet, unpaginated — used
 * for CSV export, mirroring fetchAllMenuItemsForExport above.
 */
export const fetchAllMenuItemsMissingRecipesForExport = async (
  outletId: string,
): Promise<APIResponse<MenuItemMissingRecipe[]>> => {
  try {
    const { data, error } = await supabase
      .from('menu_items_missing_recipes_view')
      .select('*')
      .eq('outlet_id', outletId)
      .order('menu_item_name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: (data || []) as MenuItemMissingRecipe[] };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch menu items missing recipes',
      data: undefined,
    };
  }
};
